import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import BlogBreadcrumb from "@/components/blog/BlogBreadcrumb";
import TagNavigation from "@/components/blog/TagNavigation";
import BlogPostCard from "@/components/blog/BlogPostCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";

interface Tag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  featureImage?: string | null;
  postCount: number;
  meta: {
    title: string;
    description: string;
  };
}

interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featureImage?: string | null;
  featureImageAlt?: string;
  publishedAt: string;
  readingTime?: number;
  primaryTag?: {
    name: string;
    slug: string;
  } | null;
  primaryAuthor?: {
    name: string;
    slug: string;
    profileImage?: string | null;
  };
}

interface AllTag {
  name: string;
  slug: string;
  postCount: number;
}

interface Pagination {
  page: number;
  pages: number;
  total: number;
  next: number | null;
  prev: number | null;
}

const BlogTag = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { getLocalizedPath } = useLocalizedPath();
  const [tag, setTag] = useState<Tag | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [allTags, setAllTags] = useState<AllTag[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine language from URL
  const isEnglish = location.pathname.startsWith("/en");
  const currentLang = isEnglish ? "en" : "th";

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch tag info
        const tagUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-tags`);
        tagUrl.searchParams.set("slug", slug);

        const tagResponse = await fetch(tagUrl.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });

        const tagData = await tagResponse.json();

        if (!tagData.success) {
          setError(tagData.error || "Tag not found");
          return;
        }

        setTag(tagData.tag);

        // Fetch all tags for navigation
        const allTagsUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-tags`);
        const allTagsResponse = await fetch(allTagsUrl.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });
        const allTagsData = await allTagsResponse.json();
        if (allTagsData.success) {
          setAllTags(allTagsData.tags || []);
        }

        // Fetch posts by tag
        const postsUrl = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-posts`);
        postsUrl.searchParams.set("tag", slug);
        postsUrl.searchParams.set("limit", "12");
        postsUrl.searchParams.set("lang", currentLang);

        const postsResponse = await fetch(postsUrl.toString(), {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        });

        const postsData = await postsResponse.json();

        if (postsData.success) {
          setPosts(postsData.posts || []);
          setPagination(postsData.pagination);
        }
      } catch (err) {
        console.error("Error fetching tag data:", err);
        setError("Failed to load tag");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [slug, currentLang]);

  const handleLoadMore = async () => {
    if (!pagination?.next || !slug) return;

    setLoadingMore(true);
    try {
      const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-blog-posts`);
      url.searchParams.set("tag", slug);
      url.searchParams.set("page", pagination.next.toString());
      url.searchParams.set("limit", "12");
      url.searchParams.set("lang", currentLang);

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error("Error loading more posts:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) {
    return (
      <>
        <SEOHead
          title="Tag Not Found | Carbonwize Blog"
          description="Tag not found"
          path="/blog"
          noIndex
        />
        <div className="min-h-screen bg-background">
          <Navbar />
          <main className="pt-24 pb-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
              <h1 className="text-4xl font-bold text-foreground mb-4">Tag Not Found</h1>
              <p className="text-muted-foreground mb-8">{error}</p>
              <Link
                to={getLocalizedPath("/blog")}
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      {tag && (
        <SEOHead
          title={`${tag.meta.title} | Carbonwize Blog`}
          description={tag.meta.description || tag.description || ""}
          path={`/blog/tag/${tag.slug}`}
          ogImage={tag.featureImage || undefined}
        />
      )}

      <div className="min-h-screen bg-background">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            {loading ? (
              <div className="space-y-8">
                <Skeleton className="h-4 w-64" />
                <div className="text-center py-8">
                  <Skeleton className="h-12 w-64 mx-auto mb-4" />
                  <Skeleton className="h-4 w-96 mx-auto" />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-card border border-border/50 rounded-2xl overflow-hidden">
                      <Skeleton className="h-48 w-full" />
                      <div className="p-6 space-y-3">
                        <Skeleton className="h-6 w-full" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : tag ? (
              <>
                <BlogBreadcrumb
                  items={[{ label: "Blog", href: getLocalizedPath("/blog") }, { label: tag.name }]}
                />

                {/* Tag Header */}
                <header className="text-center py-12 border-b border-border mb-8">
                  <h1 className="text-4xl sm:text-5xl font-extrabold text-cta mb-4">
                    {tag.name}
                  </h1>
                  {tag.description && (
                    <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                      {tag.description}
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    {tag.postCount} {tag.postCount === 1 ? "article" : "articles"}
                  </p>
                </header>

                {/* Tag Navigation */}
                {allTags.length > 0 && (
                  <div className="mb-8">
                    <TagNavigation tags={allTags.slice(0, 8)} activeTag={slug} />
                  </div>
                )}

                {/* Posts Grid */}
                <section>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {posts.map((post) => (
                      <BlogPostCard key={post.id} post={post} />
                    ))}
                  </div>

                  {posts.length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No articles found with this tag.</p>
                    </div>
                  )}

                  {pagination?.next && (
                    <div className="text-center mt-8">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? "Loading..." : "Load More"}
                      </Button>
                    </div>
                  )}
                </section>
              </>
            ) : null}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogTag;
